import { useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  formatEuro,
  formatEuroExact,
  formatInteger,
  formatPercent,
  type CreateFinancingBody,
  type FinancingType,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Combobox,
  Dialog,
  Segmented,
  Skeleton,
  TextField,
  type ComboboxOption,
} from '../../../ds/components';
import { ApiError, api, errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { createFinancing, useCommissionPreview, useCreateOptions, useInvalidateFinancings } from '../api';
import type { CommissionPreview, CreateOptions } from '../types';
import { AmountField, ReadField, TagSelect, parseRate } from './fields';
import styles from './create.module.css';

export interface CreateFinancingDialogProps {
  contactId?: string | undefined;
  dealId?: string | undefined;
  /** Preselected financier (Deal-Akte 06 „Vorschlagen"). */
  financierId?: string | undefined;
  /** Called with the new financing after „Anlegen", without it on „Abbrechen". */
  onClose: (created?: { id: string }) => void;
}

const YEARS = [5, 10, 15, 20, 25, 30] as const;
const NO_FINANCIER = '';
const TITLE_FOR_CONTACT = 'Finanzierung für einen Kontakt';
const SUBTITLE_FOR_CONTACT =
  'Ohne Deal gibt es keine Einheit. Geben Sie das Objekt frei ein. Einen Deal können Sie später verknüpfen.';
const REMAINING_DEBT = 'Restschuld';

interface ContactHit {
  id: string;
  firstName: string;
  lastName: string;
  lifecycle: { label: string };
}

function useContactSearch(query: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.contacts, 'picker', query],
    queryFn: ({ signal }) =>
      api<{ items: ContactHit[] }>(`/contacts?q=${encodeURIComponent(query)}&limit=8`, { signal }),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 30_000,
  });
}

/** The contact's active property deal (P-03: at most one) — the target of „Mit Deal verknüpfen" in 05. */
function useActiveDeal(contactId: string | null) {
  return useQuery({
    queryKey: [...queryRoots.deals, 'create-options', contactId ?? ''],
    queryFn: ({ signal }) =>
      api<{ contact: { id: string; activeDealId: string | null } }>(
        `/deals/create-options?contactId=${encodeURIComponent(contactId ?? '')}`,
        { signal },
      ),
    enabled: contactId !== null,
    retry: false,
    staleTime: 30_000,
  });
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** „Eigentumswohnung · Prüfeninger Straße 41, Regensburg" → type and address of the free-text object. */
function splitObject(text: string): { type?: string; address: string } {
  const trimmed = text.trim();
  const index = trimmed.indexOf(' · ');
  if (index <= 0) return { address: trimmed };
  return { type: trimmed.slice(0, index).trim(), address: trimmed.slice(index + 3).trim() };
}

/**
 * Finanzierungen 04 (from the deal), 05 (for a contact without deal) and 10 (the assigned financier) —
 * `GET /financings/create-options` decides the variant, `POST /financings` creates. Without `contactId`
 * and `dealId` (opened from the list) the contact is picked first.
 */
export function CreateFinancingDialog({
  contactId,
  dealId,
  financierId,
  onClose,
}: CreateFinancingDialogProps) {
  const formId = useId();
  const invalidate = useInvalidateFinancings();
  const [pickedContact, setPickedContact] = useState<ComboboxOption | null>(null);
  const [contactQuery, setContactQuery] = useState('');
  const [withDeal, setWithDeal] = useState(false);
  const pickerMode = !contactId && !dealId;
  const baseContactId = contactId ?? pickedContact?.id ?? null;
  const activeDeal = useActiveDeal(dealId ? null : baseContactId);
  const activeDealId = activeDeal.data?.contact.activeDealId ?? null;
  const effectiveDealId = dealId ?? (withDeal && activeDealId ? activeDealId : undefined);
  const options = useCreateOptions({ dealId: effectiveDealId, contactId: baseContactId ?? undefined });
  const search = useContactSearch(contactQuery, pickerMode);

  const data = options.data;
  const [type, setType] = useState<FinancingType>('annuity');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [years, setYears] = useState<number>(10);
  const [endsOn, setEndsOn] = useState('');
  const [objectText, setObjectText] = useState('');
  const [selectedFinancier, setSelectedFinancier] = useState<string>(NO_FINANCIER);
  const [rateText, setRateText] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Defaults follow the loaded variant (a switch between 05 and 04 reloads the options).
  const variantKey = data ? `${data.variant}:${data.contact.id}:${data.deal?.id ?? ''}` : '';
  useEffect(() => {
    if (!data) return;
    setType(data.deal === null && data.variant === 'contact' ? 'follow_on' : 'annuity');
    setAmountCents(data.amountProposal?.cents ?? null);
    setYears(data.fixedInterestYearsDefault);
    const candidates = data.financier.candidates;
    const preset =
      candidates.find((candidate) => candidate.userId === financierId) ??
      candidates.find((candidate) => candidate.best) ??
      null;
    setSelectedFinancier(preset?.userId ?? NO_FINANCIER);
    setRateText(data.commission.editable ? formatRateInput(data.commission.ratePct) : '');
    setErrors({});
    setBanner(null);
  }, [variantKey]);

  const ratePct = data?.commission.editable ? parseRate(rateText) : null;
  const debouncedRate = useDebounced(ratePct, 250);
  const debouncedAmount = useDebounced(amountCents, 250);
  const preview = useCommissionPreview(
    data?.variant === 'financier' ? (data.deal?.id ?? null) : null,
    debouncedRate,
    debouncedAmount,
  );

  const withoutDeal = data !== undefined && data.deal === null;
  const showFinancier =
    data !== undefined &&
    data.deal !== null &&
    (data.financier.mode === 'assign' || data.financier.mode === 'propose');

  const financierOptions = useMemo(
    () => [
      { value: NO_FINANCIER, label: 'Finanzierer offen' },
      ...(data?.financier.candidates.map((candidate) => ({
        value: candidate.userId,
        label: candidate.name,
      })) ?? []),
    ],
    [data],
  );
  const chosenCandidate = data?.financier.candidates.find(
    (candidate) => candidate.userId === selectedFinancier,
  );

  const contactOptions: ComboboxOption[] = useMemo(() => {
    const hits: ComboboxOption[] = (search.data?.items ?? []).map((hit) => ({
      id: hit.id,
      label: `${hit.firstName} ${hit.lastName}`.trim(),
      description: hit.lifecycle.label,
    }));
    if (pickedContact && !hits.some((hit) => hit.id === pickedContact.id)) hits.unshift(pickedContact);
    return hits;
  }, [search.data, pickedContact]);

  const amountTag =
    withoutDeal && (type === 'follow_on' || type === 'forward')
      ? REMAINING_DEBT
      : data?.amountProposal && amountCents === data.amountProposal.cents
        ? data.amountProposal.tag
        : null;
  const submitLabel =
    data && showFinancier && selectedFinancier !== NO_FINANCIER ? data.texts.submitLabel : 'Anlegen';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!data || pending) return;
    const next: Record<string, string> = {};
    if (amountCents === null) next['amountCents'] = 'Bitte geben Sie den Betrag an.';
    if (withoutDeal && objectText.trim() === '') next['property.address'] = 'Bitte geben Sie das Objekt an.';
    if (data.commission.editable && ratePct === null)
      next['commissionRatePct'] = 'Bitte geben Sie einen Satz zwischen 0 und 100 an.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const body: CreateFinancingBody = {
      contactId: data.contact.id,
      dealId: data.deal?.id ?? null,
      type,
      amountCents: amountCents ?? 0,
    };
    if (data.deal !== null) body.fixedInterestYears = years;
    else {
      if (endsOn) body.fixedInterestEndsOn = endsOn;
      const object = splitObject(objectText);
      body.property = { ...object };
      if (amountTag === REMAINING_DEBT) {
        body.amountSource = REMAINING_DEBT;
        body.property.remainingDebtCents = amountCents;
      }
    }
    if (showFinancier && selectedFinancier !== NO_FINANCIER) body.financierId = selectedFinancier;
    if (data.commission.editable && ratePct !== null) body.commissionRatePct = ratePct;
    setPending(true);
    setBanner(null);
    try {
      const created = await createFinancing(body);
      await invalidate(created.id);
      onClose({ id: created.id });
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setErrors(error.fields);
      else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  const loadError = options.isError ? errorMessage(options.error) : null;
  const eyebrow = data?.texts.eyebrow ?? 'Finanzierung anlegen';
  const title = data?.texts.title ?? TITLE_FOR_CONTACT;
  const subtitle = data?.texts.subtitle ?? SUBTITLE_FOR_CONTACT;
  const segments = data?.types ?? [];

  const typeField = (
    <div className={styles.field}>
      <span className={styles.label} id={`${formId}-type`}>
        Art
      </span>
      <Segmented
        label="Art"
        className={styles.segmented}
        value={type}
        items={segments.map((entry) => ({ value: entry.key, label: entry.label }))}
        onChange={setType}
      />
    </div>
  );

  return (
    <Dialog
      open
      onClose={() => onClose()}
      width={data?.variant === 'financier' ? 620 : 600}
      initialFocus="container"
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      footerNote={data?.texts.footer}
      footer={
        <>
          <Button variant="secondary" onClick={() => onClose()} disabled={pending}>
            Abbrechen
          </Button>
          {data ? (
            <Button type="submit" form={formId} loading={pending}>
              {submitLabel}
            </Button>
          ) : null}
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {pickerMode ? (
          <Combobox
            label="Kontakt"
            required
            options={contactOptions}
            value={pickedContact?.id ?? null}
            onChange={(_, option) => {
              setPickedContact(option);
              setWithDeal(false);
            }}
            onQueryChange={setContactQuery}
            filter={false}
            placeholder="Name suchen"
            emptyText={contactQuery.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben' : 'Kein Treffer'}
            hint={data?.contact.tag ?? undefined}
            error={errors['contactId']}
            autoFocus
          />
        ) : null}
        {loadError ? <Banner tone="danger">{loadError}</Banner> : null}
        {options.isFetching && !data ? (
          <div className={styles.skeleton} aria-busy="true">
            <Skeleton width="45%" />
            <Skeleton variant="block" height={40} />
            <Skeleton variant="block" height={40} />
          </div>
        ) : null}
        {data ? (
          <CreateFields
            data={data}
            formId={formId}
            pickerMode={pickerMode}
            dealSwitch={!dealId && data.variant !== 'financier'}
            withDeal={withDeal}
            canLinkDeal={activeDealId !== null}
            onWithDealChange={setWithDeal}
            typeField={typeField}
            amountCents={amountCents}
            amountTag={amountTag}
            onAmountChange={setAmountCents}
            years={years}
            onYearsChange={setYears}
            endsOn={endsOn}
            onEndsOnChange={setEndsOn}
            objectText={objectText}
            onObjectTextChange={setObjectText}
            showFinancier={showFinancier}
            financierOptions={financierOptions}
            selectedFinancier={selectedFinancier}
            financierTag={chosenCandidate?.best ? 'beste Übereinstimmung' : null}
            onFinancierChange={setSelectedFinancier}
            rateText={rateText}
            onRateTextChange={setRateText}
            ratePct={ratePct}
            preview={preview.data ?? null}
            previewError={preview.isError ? errorMessage(preview.error) : null}
            errors={errors}
          />
        ) : null}
      </form>
    </Dialog>
  );
}

function formatRateInput(pct: number): string {
  return pct.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

interface CreateFieldsProps {
  data: CreateOptions;
  formId: string;
  pickerMode: boolean;
  dealSwitch: boolean;
  withDeal: boolean;
  canLinkDeal: boolean;
  onWithDealChange: (value: boolean) => void;
  typeField: ReactNode;
  amountCents: number | null;
  amountTag: string | null;
  onAmountChange: (cents: number | null) => void;
  years: number;
  onYearsChange: (years: number) => void;
  endsOn: string;
  onEndsOnChange: (value: string) => void;
  objectText: string;
  onObjectTextChange: (value: string) => void;
  showFinancier: boolean;
  financierOptions: { value: string; label: string }[];
  selectedFinancier: string;
  financierTag: string | null;
  onFinancierChange: (value: string) => void;
  rateText: string;
  onRateTextChange: (value: string) => void;
  ratePct: number | null;
  preview: CommissionPreview | null;
  previewError: string | null;
  errors: Record<string, string>;
}

function CreateFields(props: CreateFieldsProps) {
  const { data, errors } = props;
  const financier = data.variant === 'financier';
  const withoutDeal = data.deal === null;

  const dealSegment = props.dealSwitch ? (
    <div className={styles.field}>
      <span className={styles.label} id={`${props.formId}-deal`}>
        Deal
      </span>
      <Segmented
        label="Deal"
        className={styles.segmented}
        value={props.withDeal ? 'deal' : 'none'}
        items={[
          { value: 'deal', label: 'Mit Deal verknüpfen', disabled: !props.canLinkDeal },
          { value: 'none', label: 'Ohne Deal' },
        ]}
        onChange={(value) => props.onWithDealChange(value === 'deal')}
      />
    </div>
  ) : null;

  const contactField = props.pickerMode ? null : (
    <ReadField label="Kontakt" tone="well" value={data.contact.name} tag={data.contact.tag} />
  );

  if (financier) {
    const previewAmount =
      props.preview && props.ratePct !== null ? formatEuroExact(props.preview.amountCents) : null;
    return (
      <>
        <div className={styles.pair}>
          <ReadField label="Kontakt" value={data.contact.name} />
          <ReadField label="Immobilien-Deal" value={data.deal?.line ?? '—'} />
        </div>
        {props.typeField}
        <div className={styles.pair}>
          <ReadField
            label="Betrag"
            tone="well"
            value={props.amountCents === null ? '—' : formatEuro(props.amountCents)}
            tag={data.amountProposal?.tag ?? null}
          />
          <TextField
            label="Provision an den Vertrieb"
            inputMode="decimal"
            value={props.rateText}
            error={errors['commissionRatePct']}
            hint={data.commission.hint}
            trailing={
              <span className={styles.rateTrail}>
                %{previewAmount ? <span className={styles.tag}>= {previewAmount}</span> : null}
              </span>
            }
            onChange={(event) => props.onRateTextChange(event.target.value)}
            autoFocus
          />
        </div>
        <div className={styles.distribution}>
          <div className={styles.eyebrow}>Verteilung laut Provisionseinstellungen</div>
          {props.previewError ? <Banner tone="danger">{props.previewError}</Banner> : null}
          {props.preview ? (
            <div className={styles.shares}>
              {props.preview.shares.map((share) => (
                <div key={share.userId} className={styles.shareRow}>
                  <span className={styles.shareName}>
                    <span className={styles.strong}>{share.name}</span>{' '}
                    <span className={styles.role}>{share.roleLabel}</span>
                  </span>
                  <span className={styles.sharePct}>{formatPercent(share.sharePct, 2)}</span>
                  <span className={styles.shareAmount}>{formatEuroExact(share.amountCents)}</span>
                </div>
              ))}
              <div className={`${styles.shareRow} ${styles.sumRow}`}>
                <span className={styles.shareName}>Summe</span>
                <span className={styles.sharePct}>{formatPercent(props.preview.sumPct, 2)}</span>
                <span className={styles.shareAmount}>{formatEuroExact(props.preview.sumCents)}</span>
              </div>
            </div>
          ) : (
            <Skeleton variant="block" height={80} />
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {withoutDeal ? (
        <>
          {contactField}
          {dealSegment}
          {props.typeField}
          <TextField
            label="Objekt"
            value={props.objectText}
            hint={data.texts.objectHint ?? undefined}
            error={errors['property.address'] ?? errors['property']}
            onChange={(event) => props.onObjectTextChange(event.target.value)}
          />
          <div className={styles.pair}>
            <AmountField
              label="Betrag"
              cents={props.amountCents}
              tag={props.amountTag}
              error={errors['amountCents']}
              onChange={props.onAmountChange}
            />
            <TextField
              label="Zinsbindung endet"
              type="date"
              value={props.endsOn}
              error={errors['fixedInterestEndsOn']}
              onChange={(event) => props.onEndsOnChange(event.target.value)}
            />
          </div>
        </>
      ) : (
        <>
          {props.pickerMode ? (
            <ReadField label="Deal" value={data.deal?.line ?? '—'} />
          ) : (
            <div className={styles.pair}>
              <ReadField label="Kontakt" value={data.contact.name} />
              <ReadField label="Deal" value={data.deal?.line ?? '—'} />
            </div>
          )}
          {dealSegment}
          {props.typeField}
          <div className={styles.pair}>
            <AmountField
              label="Betrag"
              cents={props.amountCents}
              tag={props.amountTag}
              autoFocus
              hint={data.amountProposal?.hint ?? null}
              error={errors['amountCents']}
              onChange={props.onAmountChange}
            />
            <TagSelect
              label="Gewünschte Zinsbindung"
              value={String(props.years)}
              options={YEARS.map((value) => ({
                value: String(value),
                label: `${formatInteger(value)} Jahre`,
              }))}
              error={errors['fixedInterestYears']}
              onChange={(value) => props.onYearsChange(Number(value))}
            />
          </div>
          {props.showFinancier ? (
            <TagSelect
              label="Finanzierer"
              value={props.selectedFinancier}
              options={props.financierOptions}
              tag={props.financierTag}
              hint={data.financier.hint}
              error={errors['financierId']}
              onChange={props.onFinancierChange}
            />
          ) : null}
          <ReadField
            label="Provision"
            value={`${formatPercent(data.commission.ratePct, 2)} vom Darlehen`}
            hint={data.commission.hint}
          />
          {data.texts.note ? <p className={styles.note}>{data.texts.note}</p> : null}
        </>
      )}
    </>
  );
}
